import { apiClient } from './api-client';

// Types based on api-contract.json

export interface OnboardingProgress {
  personId: string;
  personalInfoConfirmed: boolean;
  cardFrontUploaded: boolean;
  cardBackUploaded: boolean;
  dependentsReviewed: boolean;
  bankingConfirmed: boolean;
  onboardingCompletedAt: string | null;
}

export interface BankingRecord {
  id: string;
  personId: string;
  version: number;
  accountHolder: string;
  institutionName?: string;
  accountReference: string; // masked
  status: string;
  isCurrent: boolean;
  confirmedAt: string | null;
  documentId?: string;
}

export interface Program {
  id: string;
  slug: string;
  name: string;
  description?: string;
  status: string;
  opensAt?: string;
  closesAt?: string;
  currentRelease: ProgramRelease | null;
}

export interface ProgramRelease {
  id: string;
  version: number;
  amountPerRecipient?: number;
  eligibilityText?: string;
  adultReleaseText?: string;
  minorReleaseText?: string;
  eftConsentText?: string;
  requiresWitness?: boolean;
  signatureMethod?: string;
  legalApproved?: boolean;
  submissionPrerequisites?: string[];
}

export interface Application {
  id: string;
  programId: string;
  referenceNumber: string;
  status: string;
  updatedAt: string;
  submittedAt?: string;
  draftData?: any;
  program: {
    id: string;
    name: string;
  };
  release?: ProgramRelease;
  recipients: ApplicationRecipient[];
  corrections?: CorrectionRequest[];
}

export interface ApplicationRecipient {
  id: string;
  personId: string;
  status: string;
  paymentReference?: string;
  person: {
    id: string;
    legalFirstName: string;
    legalLastName: string;
    dateOfBirth: string;
    treatyNumber: string;
  };
}

export interface CorrectionRequest {
  id: string;
  applicationId?: string;
  category: string;
  description: string;
  requestedChange?: string;
  status: string;
  response?: string;
  memberReply?: string;
  createdAt: string;
}

export interface DashboardSummary {
  invitesSent: number;
  membersVerified: number;
  verificationsPending: number;
  fundingCounts: Record<string, number>;
  nextMeeting: {
    title: string;
    scheduledAt: string;
    registeredCount: number | null;
  } | null;
}

export interface Notification {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  readAt?: string;
  href?: string;
}

export interface Document {
  id: string;
  personId: string;
  purpose: string;
  side?: string;
  status: string;
  filename: string;
  mimeType: string;
  uploadedAt: string;
  verifiedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}

// API methods for portal expansion features

export const portalExpansionApi = {
  // Dashboard
  getOnboardingSummary: async (): Promise<DashboardSummary> => {
    const { data } = await apiClient.get('/onboarding/admin/summary');
    return data;
  },

  // Onboarding Review
  getPendingOnboarding: async (page = 1, limit = 20) => {
    const { data } = await apiClient.get('/onboarding/admin/pending', {
      params: { page, limit },
    });
    return data;
  },

  getOnboardingProgress: async (personId: string): Promise<OnboardingProgress> => {
    const { data } = await apiClient.get(`/onboarding/progress/${personId}`);
    return data;
  },

  // Dependents Review
  getPendingDependents: async (page = 1, limit = 20) => {
    const { data } = await apiClient.get('/dependents/admin/pending', {
      params: { page, limit },
    });
    return data;
  },

  verifyDependent: async (relationshipId: string, decision: 'full' | 'limited' | 'denied', notes?: string) => {
    const { data } = await apiClient.patch(`/dependents/${relationshipId}/verify`, {
      decision,
      notes,
    });
    return data;
  },

  // Documents
  getPersonDocuments: async (personId: string): Promise<Document[]> => {
    const { data } = await apiClient.get(`/documents/person/${personId}`);
    return data;
  },

  getDocument: async (documentId: string): Promise<Document> => {
    const { data } = await apiClient.get(`/documents/${documentId}`);
    return data;
  },

  getDocumentPreview: async (documentId: string): Promise<{ url: string; downloadUrl: string }> => {
    const { data } = await apiClient.get(`/documents/${documentId}`);
    return { url: data.downloadUrl, downloadUrl: data.downloadUrl };
  },

  reviewDocument: async (
    documentId: string,
    decision: 'verified' | 'rejected',
    rejectionReason?: string,
  ) => {
    const { data } = await apiClient.patch(`/documents/${documentId}/review`, {
      decision,
      rejectionReason,
    });
    return data;
  },

  // Banking Review
  getPendingBanking: async (page = 1, limit = 20) => {
    const { data } = await apiClient.get('/banking/admin/pending', {
      params: { page, limit },
    });
    return Array.isArray(data) ? { records: data, total: data.length } : data;
  },

  verifyBanking: async (
    bankingId: string,
    payload: {
      decision: 'verified' | 'rejected';
      accountHolder?: string;
      institutionNumber?: string;
      transitNumber?: string;
      accountNumber?: string;
      notes?: string;
    },
  ) => {
    const { data } = await apiClient.patch(`/banking/admin/${bankingId}`, payload);
    return data;
  },

  // Programs
  getPrograms: async (): Promise<Program[]> => {
    const { data } = await apiClient.get('/programs');
    return data.programs;
  },

  getProgramById: async (id: string): Promise<Program> => {
    const { data } = await apiClient.get(`/programs/${id}`);
    return data;
  },

  getAllPrograms: async (): Promise<Program[]> => {
    const { data } = await apiClient.get('/programs/admin/all');
    return data.programs;
  },

  createProgram: async (payload: {
    slug: string;
    name: string;
    description?: string;
    opensAt?: string;
    closesAt?: string;
  }): Promise<Program> => {
    const { data } = await apiClient.post('/programs/admin', payload);
    return data;
  },

  updateProgram: async (
    id: string,
    payload: {
      name?: string;
      description?: string;
      opensAt?: string;
      closesAt?: string;
      amountPerRecipient?: number;
      eligibilityText?: string;
      adultReleaseText?: string;
      minorReleaseText?: string;
      eftConsentText?: string;
      requiresWitness?: boolean;
      signatureMethod?: string;
      legalApproved?: boolean;
      submissionPrerequisites?: string[];
    },
  ): Promise<Program> => {
    const { data } = await apiClient.patch(`/programs/admin/${id}`, payload);
    return data;
  },

  publishProgram: async (id: string): Promise<Program> => {
    const { data } = await apiClient.post(`/programs/admin/${id}/publish`, {});
    return data;
  },

  closeProgram: async (id: string): Promise<Program> => {
    const { data } = await apiClient.post(`/programs/admin/${id}/close`, {});
    return data;
  },

  // Applications (Funding Queue)
  getAllApplications: async (page = 1, limit = 20, status?: string) => {
    const { data } = await apiClient.get('/applications/admin/all', {
      params: { page, limit, status },
    });
    return data;
  },

  getApplication: async (id: string): Promise<Application> => {
    const { data } = await apiClient.get(`/applications/${id}`);
    return data;
  },

  updateApplicationStatus: async (
    id: string,
    payload: {
      status: string;
      publicMessage?: string;
      internalNotes?: string;
    },
  ) => {
    const { data } = await apiClient.patch(`/applications/admin/${id}/status`, payload);
    return data;
  },

  updateRecipientStatus: async (
    recipientId: string,
    payload: {
      status: string;
      paymentReference?: string;
      notes?: string;
    },
  ) => {
    const { data } = await apiClient.patch(`/applications/admin/recipients/${recipientId}`, payload);
    return data;
  },

  requestInformation: async (
    applicationId: string,
    payload: {
      description: string;
      requestedChange?: string;
      personId?: string;
    },
  ) => {
    const { data } = await apiClient.post(`/applications/admin/${applicationId}/request-information`, payload);
    return data;
  },

  // Corrections
  getAllCorrections: async (page = 1, limit = 20, status?: string) => {
    const { data } = await apiClient.get('/corrections/admin/all', {
      params: { page, limit, status },
    });
    return data;
  },

  respondToCorrection: async (
    correctionId: string,
    payload: {
      status: string;
      response?: string;
    },
  ) => {
    const { data } = await apiClient.patch(`/corrections/admin/${correctionId}`, payload);
    return data;
  },

  // Notifications
  getNotifications: async () => {
    const { data } = await apiClient.get('/onboarding/notifications');
    return data;
  },

  markNotificationRead: async (id: string) => {
    const { data } = await apiClient.post(`/onboarding/notifications/${id}/read`, {});
    return data;
  },
};
